import React, { useEffect, useState } from "react";
import { EditTwoTone } from "@ant-design/icons";
import { Button, Checkbox, Descriptions, Form, Tag, theme } from "antd";
import PracticesService, {
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";

interface ReviewRequestProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  practiceId: number;
  data?: {
    apct?: boolean;
    sA100?: boolean;
    sA800?: boolean;
    vat?: boolean;
  };
  onUpdated: () => Promise<void>;
}

const ReviewRequest: React.FC<ReviewRequestProps> = ({
  isEdit,
  setIsEdit,
  practiceId,
  data,
  onUpdated,
}) => {
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();
  const { openNotification } = React.useContext(adminContext);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const isEditing = isEdit.type === PracticeUpdateTypes.reviewRequest;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        apct: !!data.apct,
        sA100: !!data.sA100,
        sA800: !!data.sA800,
        vat: !!data.vat,
      });
    }
  }, [data, form]);

  const handleOpenEdit = () => {
    if (data) {
      form.setFieldsValue({
        apct: !!data.apct,
        sA100: !!data.sA100,
        sA800: !!data.sA800,
        vat: !!data.vat,
      });
    }
    setIsEdit({ type: PracticeUpdateTypes.reviewRequest });
  };

  const handleCancel = () => {
    if (data) {
      form.setFieldsValue(data);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const params = {
        APCT: !!values.apct,
        SA100: !!values.sA100,
        SA800: !!values.sA800,
        VAT: !!values.vat,
      };

      const res = await PracticesService.updatePracticeReviewReq(
        practiceId,
        params,
      );

      if (res?.result && res?.status) {
        openNotification(
          "success",
          "Review request settings updated successfully",
        );
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await onUpdated();
      } else {
        openNotification(
          "error",
          res?.message || "Error updating review request settings",
        );
      }
    } catch {
      openNotification("error", "Error updating review request settings");
    } finally {
      setSaving(false);
    }
  };

  const renderStatusTag = (enabled?: boolean) => {
    return enabled ? (
      <Tag color="green" style={{ borderRadius: 4, margin: 0 }}>
        Enabled
      </Tag>
    ) : (
      <Tag color="default" style={{ borderRadius: 4, margin: 0 }}>
        Disabled
      </Tag>
    );
  };

  return (
    <div
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: "16px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 12,
          marginBottom: 14,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: token.colorTextHeading,
            }}
          >
            Review Request Configuration
          </span>
          <div
            style={{
              fontSize: 11,
              color: token.colorTextSecondary,
              marginTop: 1,
            }}
          >
            Automated client review verification workflows for specific
            accounting return types.
          </div>
        </div>

        {!isViewOnly && (
          <Button
            size="small"
            type="text"
            shape="circle"
            icon={<EditTwoTone />}
            onClick={handleOpenEdit}
          />
        )}
      </div>

      <Descriptions
        size="small"
        bordered={false}
        column={{ xs: 1, sm: 2, md: 2, lg: 4 }}
        labelStyle={{
          color: token.colorTextSecondary,
          fontWeight: 500,
          fontSize: 12,
        }}
        contentStyle={{
          color: token.colorText,
          fontWeight: 500,
          fontSize: 13,
        }}
      >
        <Descriptions.Item label="Accounts & Corp Tax">
          {renderStatusTag(data?.apct)}
        </Descriptions.Item>

        <Descriptions.Item label="SA100 Return">
          {renderStatusTag(data?.sA100)}
        </Descriptions.Item>

        <Descriptions.Item label="SA800 Partnership">
          {renderStatusTag(data?.sA800)}
        </Descriptions.Item>

        <Descriptions.Item label="VAT Return">
          {renderStatusTag(data?.vat)}
        </Descriptions.Item>
      </Descriptions>

      <AppDrawer
        title="Edit Review Request Settings"
        subtitle="Enable or disable automated review request workflows"
        width={460}
        open={isEditing}
        onClose={handleCancel}
        onSave={handleSave}
        saveText="Save Changes"
        saveButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            name="apct"
            valuePropName="checked"
            style={{ marginBottom: 12 }}
          >
            <Checkbox>Accounts Reports & Corporation Tax</Checkbox>
          </Form.Item>

          <Form.Item
            name="sA100"
            valuePropName="checked"
            style={{ marginBottom: 12 }}
          >
            <Checkbox>SA100 Return</Checkbox>
          </Form.Item>

          <Form.Item
            name="sA800"
            valuePropName="checked"
            style={{ marginBottom: 12 }}
          >
            <Checkbox>SA800 Partnership Return</Checkbox>
          </Form.Item>

          <Form.Item
            name="vat"
            valuePropName="checked"
            style={{ marginBottom: 12 }}
          >
            <Checkbox>VAT Return</Checkbox>
          </Form.Item>
        </Form>
      </AppDrawer>
    </div>
  );
};

export default ReviewRequest;
